import { mainnet, sepolia } from 'viem/chains'
import { toBigNumber } from '@injectivelabs/utils'
import { Web3Exception } from '@injectivelabs/exceptions'
import { http, erc20Abi, createPublicClient } from 'viem'
import { isTestnetOrDevnet } from '@injectivelabs/networks'
import { injToken, injErc20Token } from '@shared/data/token'
import { injectivePeggyAddress } from '@/app/data/web3'
import { peggyDenomToContractAddress } from '@/app/services/web3/utils'
import type { Address, PublicClient } from 'viem'
import type { Network } from '@injectivelabs/networks'

/**
 * Preparing and broadcasting
 * Ethereum transactions
 */
export class Web3Client {
  private client: undefined | PublicClient

  private network: Network

  private rpc: string

  constructor({ rpc, network }: { rpc: string; network: Network }) {
    this.rpc = rpc
    this.network = network
  }

  async fetchTokenBalanceAndAllowance({
    address,
    contractAddress
  }: {
    address: string
    contractAddress: string
  }): Promise<{ balance: string; allowance: string }> {
    const { network } = this

    if (
      !contractAddress.startsWith('peggy') &&
      !contractAddress.startsWith('0x') &&
      contractAddress !== injToken.denom
    ) {
      return {
        balance: toBigNumber(0).toFixed(),
        allowance: toBigNumber(0).toFixed()
      }
    }

    try {
      const client = this.getClient()
      const tokenAddress = peggyDenomToContractAddress(contractAddress)
      const tokenContractAddress = (
        tokenAddress === injToken.denom ? injErc20Token.address : tokenAddress
      ) as Address

      const [balance, allowance] = await client.multicall({
        contracts: [
          {
            address: tokenContractAddress,
            abi: erc20Abi,
            functionName: 'balanceOf',
            args: [address as Address]
          },
          {
            address: tokenContractAddress,
            abi: erc20Abi,
            functionName: 'allowance',
            args: [
              address as Address,
              injectivePeggyAddress[network] as Address
            ]
          }
        ]
      })

      return {
        balance: toBigNumber(balance.result?.toString() || 0).toFixed(),
        allowance: toBigNumber(allowance.result?.toString() || 0).toFixed()
      }
    } catch {
      return {
        balance: toBigNumber(0).toFixed(),
        allowance: toBigNumber(0).toFixed()
      }
    }
  }

  async fetchTokenMetaData(address: string) {
    const client = this.getClient()

    try {
      const [name, symbol, decimals] = await client.multicall({
        contracts: [
          {
            address: address as Address,
            abi: erc20Abi,
            functionName: 'name'
          },
          {
            address: address as Address,
            abi: erc20Abi,
            functionName: 'symbol'
          },
          {
            address: address as Address,
            abi: erc20Abi,
            functionName: 'decimals'
          }
        ]
      })

      return {
        name: name.result || null,
        symbol: symbol.result || null,
        decimals: decimals.result || null,
        logo: null
      }
    } catch (e: unknown) {
      throw new Web3Exception(e as any)
    }
  }

  async fetchEthBridgeFee() {
    const client = this.getClient()

    try {
      const [gasPrice, maxFeePerGas, maxPriorityFeePerGas, latestBlock] =
        await Promise.all([
          client.getGasPrice(),
          client
            .estimateFeesPerGas()
            .then((fees) => fees.maxFeePerGas)
            .catch(() => null),
          client
            .estimateFeesPerGas()
            .then((fees) => fees.maxPriorityFeePerGas)
            .catch(() => null),
          client.getBlock({ blockTag: 'latest' }).catch(() => null)
        ])

      return {
        gasPrice,
        maxFeePerGas,
        maxPriorityFeePerGas,
        lastBaseFeePerGas: latestBlock?.baseFeePerGas ?? null
      }
    } catch (e: unknown) {
      throw new Web3Exception(e as any)
    }
  }

  async fetchEtherBalance(address: string) {
    const client = this.getClient()

    try {
      const ethBalance = await client.getBalance({
        address: address as Address
      })

      return ethBalance.toString()
    } catch (e: unknown) {
      throw new Web3Exception(e as any)
    }
  }

  private getClient(): PublicClient {
    if (this.client) {
      return this.client
    }

    const { rpc, network } = this

    this.client = createPublicClient({
      chain: !isTestnetOrDevnet(network) ? mainnet : sepolia,
      transport: http(rpc)
    })

    return this.client
  }
}
